import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldDate, FieldToggle,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

// Sayım Tanımı (demirbaş sayım emri) detay — GET-by-id YOK; tek kayıt nav-state row ile,
// yoksa listeyi (take=500) çekip id ile bulunarak yüklenir. CRUD POST / PUT/{id} / DELETE/{id}.
const API_BASE = '/api/asset-count-orders';

const TYPE_OPTIONS = [
  { value: 'FIRST', label: 'İlk Sayım' },
  { value: 'CONTROL', label: 'Kontrol Sayımı' },
];
const LIFECYCLE_OPTIONS = [
  { value: '0', label: 'Taslak' },
  { value: '1', label: 'Aktif' },
  { value: '2', label: 'Pasif' },
];
const LIFECYCLE_META = {
  0: { label: 'Taslak', tone: 'neutral' },
  1: { label: 'Aktif', tone: 'ok' },
  2: { label: 'Pasif', tone: 'bad' },
};

const EMPTY_FORM = {
  textId: '',
  title: '',
  countType: 'FIRST',
  countDate: '',
  isMandatory: false,
  requireScanForOwners: false,
  blockSaleIfMissing: false,
  autoApprove: false,
  lifecycleStatus: '0',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function toLocalDateInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch { return ''; }
}
function fromLocalDateInput(value) {
  if (!value) return null;
  try { return new Date(`${value}T00:00:00`).toISOString(); } catch { return null; }
}

function rowToForm(row) {
  return {
    textId: pick(row, 'textId', 'TextId') || '',
    title: pick(row, 'title', 'Title') || '',
    countType: pick(row, 'countType', 'CountType') || 'FIRST',
    countDate: toLocalDateInput(pick(row, 'countDate', 'CountDate')),
    isMandatory: !!pick(row, 'isMandatory', 'IsMandatory'),
    requireScanForOwners: !!pick(row, 'requireScanForOwners', 'RequireScanForOwners'),
    blockSaleIfMissing: !!pick(row, 'blockSaleIfMissing', 'BlockSaleIfMissing'),
    autoApprove: !!pick(row, 'autoApprove', 'AutoApprove'),
    lifecycleStatus: String(pick(row, 'lifecycleStatus', 'LifecycleStatus') ?? 0),
  };
}

export function AssetCountOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Sayım Tanımı' : 'Sayım Tanımı Detayı', 'Demirbaş sayım emri — ilk sayım / kontrol sayımı.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const applyAudit = (r) => setAudit({
    createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'),
    createdDate: pick(r, 'createdDate', 'CreatedDate'),
    updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'),
    updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate'),
  });

  // GET-by-id yok: önce nav-state row, yoksa listeyi çekip id ile bul.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    const stateRow = location.state?.row;
    if (stateRow && String(pick(stateRow, 'id', 'Id')) === String(id)) {
      setForm(rowToForm(stateRow));
      applyAudit(stateRow);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Sayım tanımı alınamadı (${res.status})`);
      const data = unwrap(await res.json());
      const items = pick(data, 'items', 'Items') ?? (Array.isArray(data) ? data : []);
      const row = (Array.isArray(items) ? items : []).find((r) => String(pick(r, 'id', 'Id')) === String(id));
      if (!row) throw new Error('Kayıt bulunamadı');
      setForm(rowToForm(row));
      applyAudit(row);
    } catch (err) {
      showToast('error', err.message || 'Sayım tanımı alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildBody = () => ({
    textId: (form.textId || '').trim() || null,
    title: (form.title || '').trim(),
    countType: form.countType,
    countDate: fromLocalDateInput(form.countDate),
    isMandatory: !!form.isMandatory,
    requireScanForOwners: !!form.requireScanForOwners,
    blockSaleIfMissing: !!form.blockSaleIfMissing,
    autoApprove: !!form.autoApprove,
    lifecycleStatus: Number(form.lifecycleStatus) || 0,
  });

  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!String(form.title || '').trim()) { showToast('error', 'Başlık zorunlu.'); return; }
    if (!form.countType) { showToast('error', 'Sayım tipi zorunlu.'); return; }
    setSaving(true);
    try {
      const url = isNew ? API_BASE : `${API_BASE}/${id}`;
      const res = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(buildBody()) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `İşlem başarısız (${res.status})`);
      if (isNew) {
        if (andNew) {
          showToast('success', 'Sayım tanımı eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Sayım tanımı oluşturuldu.');
        setTimeout(() => navigate('/asset-count-orders'), 400);
        return;
      }
      showToast('success', 'Sayım tanımı güncellendi.');
      setTimeout(() => navigate('/asset-count-orders'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();
  const handleClone = () => navigate('/asset-count-orders/new', { state: { clone: { ...form } } });

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Sayım tanımı silindi.');
      setTimeout(() => navigate('/asset-count-orders'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const title = isNew ? 'Yeni Sayım Tanımı' : (form.title || 'Sayım Tanımı Detayı');
  const stMeta = LIFECYCLE_META[Number(form.lifecycleStatus)] || { label: '-', tone: 'neutral' };
  const statusBadge = !isNew && <StatusBadge tone={stMeta.tone}>{stMeta.label}</StatusBadge>;

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/asset-count-orders')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni sayım tanımı aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Sayım Tanımı', to: '/asset-count-orders' }, { label: isNew ? 'Yeni Sayım Tanımı' : 'Sayım Tanımı Detayı', current: true }]}
      status={statusBadge}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'AssetCountOrder', entityId: id }}
      actions={actions}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="clock" title="Sayım Emri" subtitle="Başlık, tip, tarih ve durum">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="title" label="Başlık" req span placeholder="örn. 2026 Q3 Kontrol Sayımı" />
            <FieldSelect form={form} set={set} k="countType" label="Sayım Tipi" options={TYPE_OPTIONS} req />
            <FieldDate form={form} set={set} k="countDate" label="Sayım Tarihi" />
            <FieldText form={form} set={set} k="textId" label="Kod (TextId)" />
            <FieldSelect form={form} set={set} k="lifecycleStatus" label="Durum" options={LIFECYCLE_OPTIONS} req />
          </Grid>
        </Section>

        <Section icon="shield" title="Kurallar" subtitle="Zorunluluk, tarama ve onay davranışı">
          <Grid cols={2}>
            <FieldToggle form={form} set={set} k="isMandatory" label="Zorunlu Sayım" hint="Temsilci bu sayımı atlayamaz" />
            <FieldToggle form={form} set={set} k="requireScanForOwners" label="Sahiplerde Tarama Zorunlu" hint="Demirbaş sahibi noktalarda barkod/seri okutulmalı" />
            <FieldToggle form={form} set={set} k="blockSaleIfMissing" label="Eksikse Satışı Blokla" hint="Sayım eksikse siparişe izin verme" />
            <FieldToggle form={form} set={set} k="autoApprove" label="Otomatik Onay" hint="Gelen sayımlar onay beklemeden onaylanır" />
          </Grid>
        </Section>
      </div>

      {deleteConfirm && (
        <SubModal
          title="Sayım Tanımı Sil"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.title || title}”</strong> sayım tanımını silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default AssetCountOrderDetailPage;
